import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, ExternalLink, MoreVertical } from "lucide-react";

const PaymentActionMenu = ({ onViewDetails, paymentLink }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ style: {} });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const calculatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = paymentLink ? 96 : 52;
      const menuWidth = 196;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight + 12;

      setPosition({
        style: {
          position: "fixed",
          top: openUp ? rect.top - menuHeight - 6 : rect.bottom + 6,
          left: Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12),
          width: menuWidth,
          zIndex: 9999,
        },
      });
    };

    calculatePosition();
    window.addEventListener("scroll", calculatePosition, true);
    window.addEventListener("resize", calculatePosition);

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", calculatePosition, true);
      window.removeEventListener("resize", calculatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, paymentLink]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center justify-center w-8 h-8 text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-700 transition-colors"
        aria-label="Payment actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen &&
        createPortal(
          <AnimatePresence>
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              style={position.style}
              className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-xl ring-1 ring-black/5"
            >
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onViewDetails();
                }}
                className="flex gap-2.5 items-center w-full px-4 py-3 text-sm font-medium text-gray-700 hover:bg-[#FF6C6C]/5 hover:text-[#FF6C6C] transition-colors"
              >
                <Eye className="w-4 h-4" />
                View Details
              </button>
              {paymentLink && (
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="flex gap-2.5 items-center w-full px-4 py-3 text-sm font-medium text-gray-700 border-t border-gray-50 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open Payment Link
                </a>
              )}
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

export default PaymentActionMenu;
